import { z } from 'zod';

import {
  INVALID_FORMAT_MESSAGE,
  S3_KEY,
  S3_UPLOAD_ERROR,
  S3_DELETE_ERROR,
  imageFormatSchema,
  notFoundMessageSchema,
} from './formats.js';

/**
 * `org.images` request/response schemas — `POST /image` and `DELETE /image`.
 *
 * Derived from the images-backend executable contract
 * (`contracts-legacy/images-backend/schemas.ts`): the request shapes are what the
 * legacy handlers read off the JSON body, and the response unions are the decoded
 * bodies of every variant the route can emit. Field names stay on the legacy wire
 * spelling (`image_data`, `file_name`, …) so the contract pins the legacy shapes
 * bug-for-bug and the caller cutover is a re-point, not a rename. Every object is
 * `.strict()` — a fifth key on any envelope is a contract failure, not a
 * passthrough.
 */

// ----------------------------------------------------------------	inputs ----

/**
 * `POST /image` request. `image_type` is deliberately `z.string()`, NOT the FE
 * enum `fundraiser|event|logo`: the backend does not validate it (dossier §6.3)
 * and any string reaches the S3 key prefix. The FE restricts the enum client-side.
 */
export const imagesUploadInputSchema = z
  .object({
    /** Required on the wire; a value that fails the name match yields 403. */
    nonprofitName: z.string(),
    /** Base64, with an optional data-URL prefix stripped server-side. */
    image_data: z.string(),
    image_name: z.string(),
    /** Unvalidated server-side — see the note above. */
    image_type: z.string(),
    /**
     * `valid_formats` closed enum; omitted → server defaults to `png`;
     * `jpeg` is normalized to `jpg` before the key is built (dossier §6.4).
     */
    image_format: imageFormatSchema.optional(),
  })
  .strict();
export type ImagesUploadInput = z.infer<typeof imagesUploadInputSchema>;

/**
 * `DELETE /image` request. Legacy accepts the parameters from a JSON body OR the
 * query string (body wins; neither → 400 `Missing request parameters`) — that is
 * transport, not schema, so the tRPC input is one object. `image_name` and
 * `image_type` are guarded truthy by the handler (`delete_method`'s 400).
 */
export const imagesDeleteInputSchema = z
  .object({
    nonprofitName: z.string(),
    image_name: z.string(),
    image_type: z.string(),
    /**
     * Supplied → validated against `valid_formats` (400 otherwise) and only that
     * extension is deleted. Omitted → the handler probes all 11 stored extensions
     * in a fixed order and deletes the FIRST hit (dossier §6.9-images).
     */
    image_format: imageFormatSchema.optional(),
  })
  .strict();
export type ImagesDeleteInput = z.infer<typeof imagesDeleteInputSchema>;

// --------------------------------------------------------------- outputs ----

/**
 * The five bodies API Gateway (HTTP API, payload 2.0) generates WITHOUT invoking
 * the handler. These are TRANSPORT-level legacy variants: the tRPC transport
 * replaces them with typed errors, so they are deliberately NOT members of the
 * route output unions below. They are carried because the dashboard's
 * `responseData.error || 'Failed to upload image'` fallback exists exactly for
 * them (a gateway body has `message` where the FE reads `error` — the recurring
 * 10 MB `413` upload failure surfaces as the generic fallback), and the parity
 * recorder needs the pin.
 */
export const legacyGatewayMessageBodySchema = z.union([
  z.object({ message: z.literal('Unauthorized') }).strict(),
  z.object({ message: z.literal('Forbidden') }).strict(),
  z.object({ message: z.literal('Request Entity Too Large') }).strict(),
  z.object({ message: z.literal('Internal Server Error') }).strict(),
  z.object({ message: z.literal('Service Unavailable') }).strict(),
]);

/** `POST /image` 200 — built with no CORS headers on the Lambda envelope; the body is the S3 key just written. */
export const imagesUploadSuccessSchema = z
  .object({
    message: z.literal('Image uploaded successfully'),
    /** Dossier §6.3 — the S3 key contract (see `S3_KEY` for the pin's shape). */
    file_name: z.string().regex(S3_KEY),
  })
  .strict();

/** `POST /image` 400 — the whole message is a constant (join over the 12-element list). */
export const imagesUploadBadFormatSchema = z
  .object({ error: z.literal(INVALID_FORMAT_MESSAGE) })
  .strict();

/** `POST /image` 400 — decoded bytes are missing, malformed, or not the declared image format. */
export const imagesUploadInvalidDataSchema = z
  .object({ error: z.string().startsWith('Invalid image data:') })
  .strict();

/** `POST /image` 403 — name mismatch, `readOnly`, or a degraded org lookup (dossier §6.1/§6.2). */
export const imagesUploadForbiddenSchema = z
  .object({ error: z.literal('Unauthorized') })
  .strict();

/** `POST /image` 500 — `except ClientError` around `put_object`; only the prefix is pinned. */
export const imagesUploadS3ErrorSchema = z
  .object({ error: z.string().regex(S3_UPLOAD_ERROR) })
  .strict();

/**
 * `POST /image` response — union of every decoded body the route's handler can
 * emit. Exactly one member matches any real legacy response.
 */
export const imagesUploadResponseSchema = z.union([
  imagesUploadSuccessSchema,
  imagesUploadBadFormatSchema,
  imagesUploadInvalidDataSchema,
  imagesUploadForbiddenSchema,
  imagesUploadS3ErrorSchema,
]);
export type ImagesUploadResponse = z.infer<typeof imagesUploadResponseSchema>;

/** `DELETE /image` 200. */
export const imagesDeleteSuccessSchema = z
  .object({
    message: z.literal('Image deleted successfully'),
    file_name: z.string().regex(S3_KEY),
  })
  .strict();

/**
 * `DELETE /image` 400 — three distinct literals plus the shared invalid-format
 * constant, all out of the same validation prologue. The enum is what makes it
 * strict: a fifth string on a 400 is a contract failure.
 */
export const imagesDeleteBadRequestSchema = z
  .object({
    error: z.enum([
      'Missing request parameters',
      'Missing nonprofitName parameter',
      'Missing image_name or image_type parameter',
      INVALID_FORMAT_MESSAGE,
    ]),
  })
  .strict();

/** `DELETE /image` 403 — same envelope as the upload 403. */
export const imagesDeleteForbiddenSchema = z
  .object({ error: z.literal('Unauthorized') })
  .strict();

/**
 * A value the delete handler's only input gate (`if not image_name or not
 * image_type`) lets through. Python truthiness is what makes this a CLOSED set:
 * `false`, `0`, `null`, `""`, `[]` and `{}` can never reach the 404 echo.
 * Deliberately NOT `z.any()` — that would admit the falsy values the guard
 * provably rejects and make the pin unfalsifiable.
 */
const truthyJsonValue = z.union([
  z.string().min(1),
  z.number().refine((n) => n !== 0, {
    message: 'a zero number is falsy in Python and cannot pass the guard',
  }),
  z.literal(true),
  z.array(z.unknown()).nonempty(),
  z.record(z.unknown()).refine((o) => Object.keys(o).length > 0, {
    message: 'an empty object is falsy in Python and cannot pass the guard',
  }),
]);

/**
 * `DELETE /image` 404 — the caller's `image_name` and `image_type` are echoed
 * back VERBATIM from the parsed request body with no coercion (a JSON number in
 * the request is a JSON number in the echo — bug-for-bug, deviation D2), and the
 * message is closed at the 12 strings derived from `STORED_IMAGE_FORMATS`.
 */
export const imagesDeleteNotFoundSchema = z
  .object({
    error: notFoundMessageSchema,
    image_name: truthyJsonValue,
    image_type: truthyJsonValue,
  })
  .strict();

/** `DELETE /image` 500 — `except ClientError` around the probe loop / `delete_object`. */
export const imagesDeleteS3ErrorSchema = z
  .object({ error: z.string().regex(S3_DELETE_ERROR) })
  .strict();

/** `DELETE /image` response — union of every decoded body the route's handler can emit. */
export const imagesDeleteResponseSchema = z.union([
  imagesDeleteSuccessSchema,
  imagesDeleteBadRequestSchema,
  imagesDeleteForbiddenSchema,
  imagesDeleteNotFoundSchema,
  imagesDeleteS3ErrorSchema,
]);
export type ImagesDeleteResponse = z.infer<typeof imagesDeleteResponseSchema>;
